import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Loader2, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Initialize Stripe with environment variable
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ orderData, affiliateCouponCode, language }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');

  useEffect(() => {
    createPaymentIntent();
  }, [affiliateCouponCode]);

  const createPaymentIntent = async () => {
    try {
      const response = await axios.post(`${API}/orders/create-payment-intent`, {
        template_id: orderData.templateId,
        subscription_plan: orderData.subscriptionPlan,
        subscription_plan_id: orderData.subscriptionPlanId,  // NEW: Send plan ID
        subscription_plan_duration: orderData.subscriptionPlanDuration,
        custom_message: orderData.customMessage,
        recipient_email: orderData.recipientEmail,
        recipient_name: orderData.recipientName,
        buyer_email: orderData.buyerEmail,
        buyer_name: orderData.buyerName,
        delivery_datetime: orderData.deliveryDatetime,
        delivery_timezone: orderData.deliveryTimezone,
        amount: orderData.amount,
        affiliate_coupon_code: affiliateCouponCode || null,
        language: language || 'en'  // Pass language to backend
      });
      
      setClientSecret(response.data.clientSecret);
      setPaymentIntentId(response.data.paymentIntentId);
    } catch (error) {
      console.error('Error creating payment intent:', error);
      setError(error.response?.data?.detail || 'Failed to initialize payment');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: orderData.buyerName,
            email: orderData.buyerEmail
          }
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Confirm order on backend
        const confirmResponse = await axios.post(
          `${API}/orders/confirm?payment_intent_id=${paymentIntent.id}`
        );
        
        navigate(`/confirmation/${confirmResponse.data.order_id}`);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.response?.data?.detail || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#000',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#EF4444',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-gray-50 p-6 rounded-lg">
        <label className="block text-sm font-semibold text-black mb-3">
          Card Information
        </label>
        <div className="bg-white p-4 rounded-lg border border-gray-300">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="payment-error">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing || !clientSecret}
        className="w-full px-6 py-4 bg-[#1E88E5] text-white text-lg font-semibold rounded-lg hover:bg-[#1565C0] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        data-testid="complete-payment-btn"
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Processing...</span>
          </>
        ) : (
          <>
            <Lock className="w-5 h-5" />
            <span>Complete Payment</span>
          </>
        )}
      </button>

      <p className="text-sm text-gray-600 text-center">
        <Lock className="w-4 h-4 inline mr-1" />
        Secured by Stripe. Your payment information is encrypted.
      </p>
    </form>
  );
};

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const orderData = location.state;
  
  const [affiliateCode, setAffiliateCode] = useState('');
  const [affiliateApplied, setAffiliateApplied] = useState(null);
  const [affiliateError, setAffiliateError] = useState('');
  const [checkingAffiliate, setCheckingAffiliate] = useState(false);

  // Detect language from URL
  const getLangFromURL = () => {
    const path = location.pathname;
    if (path.includes('-ru')) return 'ru';
    if (path.includes('-kz')) return 'kz';
    return 'en';
  };

  useEffect(() => {
    if (!orderData) {
      const urlLang = getLangFromURL();
      const suffix = urlLang === 'en' ? '' : `-${urlLang}`;
      navigate(`/browse${suffix}`);
    }
    
    const urlLang = getLangFromURL();
    if (i18n.language !== urlLang) {
      i18n.changeLanguage(urlLang);
    }
  }, [orderData, navigate, location.pathname]);

  const handleApplyAffiliate = async () => {
    if (!affiliateCode.trim()) return;
    
    setCheckingAffiliate(true);
    setAffiliateError('');
    
    try {
      const response = await axios.post(`${API}/affiliate-coupons/validate`, {
        code: affiliateCode,
        original_amount: orderData.amount
      });
      
      setAffiliateApplied(response.data);
    } catch (error) {
      setAffiliateError(error.response?.data?.detail || 'Invalid affiliate code');
      setAffiliateApplied(null);
    } finally {
      setCheckingAffiliate(false);
    }
  };

  const handleRemoveAffiliate = () => {
    setAffiliateCode('');
    setAffiliateApplied(null);
    setAffiliateError('');
  };

  if (!orderData) {
    return null;
  }

  const finalAmount = affiliateApplied ? affiliateApplied.final_amount : orderData.amount;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-black mb-8 text-center">Complete Your Order</h1>
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-black">Order Summary</h2>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <div className="border-b pb-4">
                <p className="text-sm text-gray-600">Gift Card Design</p>
                <p className="font-semibold text-black">{orderData.template.name}</p>
              </div>
              
              <div className="border-b pb-4">
                <p className="text-sm text-gray-600">Subscription Plan</p>
                <p className="font-semibold text-black">{orderData.subscriptionPlan}</p>
              </div>
              
              <div className="border-b pb-4">
                <p className="text-sm text-gray-600">Recipient</p>
                <p className="font-semibold text-black">{orderData.recipientName}</p>
                <p className="text-sm text-gray-600">{orderData.recipientEmail}</p>
              </div>
              
              <div className="border-b pb-4">
                <p className="text-sm text-gray-600">Your Message</p>
                <p className="text-sm italic text-gray-700">"{orderData.customMessage}"</p>
              </div>
              
              <div className="border-b pb-4">
                <p className="text-sm text-gray-600">Delivery</p>
                <p className="font-semibold text-black">
                  {orderData.deliveryDatetime ? (
                    new Date(orderData.deliveryDatetime).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  ) : 'Immediate'} {orderData.deliveryTimezone && `(${orderData.deliveryTimezone})`}
                </p>
              </div>
              
              <div className="pt-4">
                <div className="flex justify-between items-center text-lg font-semibold border-t pt-4">
                  <span className="text-gray-700">Subtotal</span>
                  <span className="text-black">${orderData.amount}</span>
                </div>
                
                {affiliateApplied && (
                  <div className="flex justify-between items-center text-lg text-green-600 mt-2">
                    <span>Affiliate Discount ({affiliateApplied.discount_percentage}%)</span>
                    <span>-${affiliateApplied.discount_amount}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center text-xl font-bold mt-3">
                  <span className="text-black">Total</span>
                  <span className="text-[#1E88E5]">${finalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            {/* Affiliate Coupon Section */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-black mb-3">Have an Affiliate Code?</h3>
              {!affiliateApplied ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={affiliateCode}
                    onChange={(e) => setAffiliateCode(e.target.value.toUpperCase())}
                    placeholder="Enter affiliate code"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                    data-testid="affiliate-code-input"
                  />
                  <button
                    onClick={handleApplyAffiliate}
                    disabled={checkingAffiliate || !affiliateCode.trim()}
                    className="px-6 py-2 bg-[#1E88E5] text-white rounded-lg hover:bg-[#1565C0] transition-colors disabled:bg-gray-400"
                  >
                    {checkingAffiliate ? 'Checking...' : 'Apply'}
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                  <div>
                    <p className="font-semibold text-green-700">{affiliateApplied.affiliate_title}</p>
                    <p className="text-sm text-green-600">{affiliateApplied.discount_percentage}% discount applied</p>
                  </div>
                  <button
                    onClick={handleRemoveAffiliate}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              )}
              {affiliateError && (
                <p className="text-red-600 text-sm mt-2">{affiliateError}</p>
              )}
            </div>
          </div>

          {/* Payment Form */}
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-black">{t('checkout.paymentDetails')}</h2>
            <Elements stripe={stripePromise}>
              <CheckoutForm 
                orderData={orderData} 
                affiliateCouponCode={affiliateApplied ? affiliateCode : null} 
                language={orderData?.language || getLangFromURL()}
              />
            </Elements>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Checkout;
